
import Vue from 'vue';
import Component from 'vue-class-component';
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import SectionRequirementHeading from "@/features/applications/components/SectionRequirementHeading.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {
    ApiException,
    ApplicationViewModel, factory, MakeRequirementProcessingDecisionRequest,
    PaymentConfirmationRequest, PaymentMethodWithDescription, PaymentRefundRequest,
    PaymentViewModel, ProcessingDecisionType, RequirementsClient, ResultError,
    StateFeeRequirementViewModel
} from "@/api-client";
import {Prop} from "vue-property-decorator";
import {
    PaymentDetailsProps,
    StateFeeOfficialProps,
} from "@/features/applications/components/requirements/stateFee/StateFeeComponentProps";
import PaymentConfirmationForm
    from "@/features/applications/components/requirements/stateFee/PaymentConfirmationForm.vue";
import {PaymentMethodOption} from "@/features/applications/components/requirements/stateFee/PaymentMethodOption";
import RefundPaymentModal from "@/features/applications/components/requirements/stateFee/RefundPaymentModal.vue";
import PaymentDetails from "@/features/applications/components/requirements/stateFee/PaymentDetails.vue";
import toasts from "@/components/Toasts";

@Component({
    components: {
        HeadingWave,
        SectionRequirementHeading,
        HeadingGroup,
        PaymentConfirmationForm,
        PaymentDetails,
        RefundPaymentModal
    },
})
export default class StateFeeOfficial extends Vue {
    @Prop({required: true})
    props!: StateFeeOfficialProps;

    payment: PaymentViewModel = factory.create(PaymentViewModel);

    requirement!: StateFeeRequirementViewModel;
    application!: ApplicationViewModel; // TODO: Constrain this.

    paymentMethodModels: PaymentMethodWithDescription[] = [];
    paymentDetailsProps: PaymentDetailsProps | null = null;
    
    paymentConfirmationErrors: ResultError = ResultError.createEmpty();

    async created() {
        this.requirement = this.props.requirement;
        this.application = this.props.application;
        this.paymentDetailsProps = new PaymentDetailsProps(
            this.payment,
            this.application,
            this.requirement,
            false,
            true);

        await new RequirementsClient()
            .ignoreNotFound()
            .paymentDetails(this.application.id, this.requirement.id)
            .then(result => {
                this.payment = result.payment;
                this.paymentDetailsProps!.payment = result.payment;

                if (result.validationError.hasMessages()) {
                    if (!result.payment.isRefunded) // No need to show validation errors if payment has been refunded
                      this.$emit("error-happened", result.validationError);
                }
            }).catch((error: ApiException) => {
                if (error.status == 404)
                    return;
                throw error;
            });

        (await new RequirementsClient()
            .paymentMethods(this.application.id, this.requirement.id))
            .forEach(x => {
                if (x.paymentMethod != "BankLink") { // Hide bank link option since payment confirmation will be automatically created with bank link payment
                    this.paymentMethodModels.push(x);
                }
            });
    }

    get showPaymentConfirmationForm() {
        return this.payment && !this.payment.isConfirmed && this.paymentMethodOptions;
    }

    get paymentMethodOptions() {
        return this.paymentMethodModels.map(x => new PaymentMethodOption(x.paymentMethod, x.description));
    }

    async confirmPayment(paymentConfirmation: PaymentConfirmationRequest): Promise<void> {
        await new RequirementsClient().confirmPayment(paymentConfirmation, this.application.id, this.requirement.id)
            .then(() => {
                new RequirementsClient().decide(
                    new MakeRequirementProcessingDecisionRequest({comment: "", requirementDecision: ProcessingDecisionType.Accepted}), 
                    this.application.id,
                    this.requirement.id)
                toasts.success(this.$ls.Global_Toast_AddSuccess)
            }).then(() => this.$emit("application-changed"))
            .catch(error => this.setValidationErrors(error, this.$ls.Global_Toast_FormAddFailure, "paymentConfirmationErrors"));
    }

    async refundPayment(paymentRefund: PaymentRefundRequest): Promise<void> {
        await new RequirementsClient()
            .refundPayment(paymentRefund, this.application.id, this.requirement.id)
            .then(() => this.$emit("application-changed"),
                error => this.$emit("error-happened", error));
    }

    async removePaymentConfirmation(): Promise<void> {
        await new RequirementsClient()
            .removePaymentConfirmation(this.application.id, this.requirement.id)
            .then(() => this.$emit("application-changed"),
                error => this.$emit("error-happened", error));
    }
}
